import React, { useCallback, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { bandChipSx, bandTone, normalizeSortOrder } from './assessmentUiUtils';
import { getUniqueScoreValues, scoreToneFromValue, scoreToggleButtonSx } from './scoreUtils';

function pickBandLabel(total, bandsSorted) {
  if (!Number.isFinite(Number(total))) return null;
  const t = Number(total);

  for (const b of bandsSorted || []) {
    if (!b) continue;
    const mn = b.min_score === null || b.min_score === undefined ? null : Number(b.min_score);
    const mx = b.max_score === null || b.max_score === undefined ? null : Number(b.max_score);

    const geMin = mn === null ? true : t >= mn;
    const leMax = mx === null ? true : t <= mx;
    if (geMin && leMax) return b.label || null;
  }
  return null;
}

export default function AssessmentTableView({
  students,
  sortedCriteria,
  focusedCriterionId,
  itemsByKey,
  onSetScore,
  onFillUnsetScores,
  readOnly,
  savingBulk,
  isSavingScore,
  bands,
  containerSx,
  printable = false,
}) {
  const [headerFillByCriterionId, setHeaderFillByCriterionId] = useState({});

  const tableGridSx = useMemo(
    () => ({
      tableLayout: printable ? { xs: 'auto', sm: 'fixed' } : 'fixed',
      width: printable ? { xs: 'max-content', sm: '100%' } : 'max-content',
      minWidth: '100%',

      // Modern, clean grid lines (vertical + horizontal)
      '& .MuiTableRow-root > .MuiTableCell-root': {
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
      '& .MuiTableRow-root > .MuiTableCell-root:not(:last-of-type)': {
        borderRight: '1px solid',
        borderColor: 'divider',
      },
      '& .MuiTableBody-root .MuiTableRow-root:last-child > .MuiTableCell-root': {
        borderBottom: 'none',
      },

      // Header background should be distinct from zebra rows
      '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.08)',
      },

      // Subtle zebra striping to improve scanning across many columns
      '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[50] : 'rgba(255,255,255,0.04)'),
      },
      '& .MuiTableBody-root .MuiTableRow-root:hover': {
        backgroundColor: 'action.hover',
      },
    }),
    [printable]
  );

  const stickyStudentCellHeadSx = useMemo(
    () => {
      if (printable) return {};
      return {
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: 4,
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.08)',
        borderRight: '1px solid',
        borderColor: 'divider',
      };
    },
    [printable]
  );

  const stickyStudentCellBodySx = useMemo(
    () => {
      if (printable) return {};
      return {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      };
    },
    [printable]
  );

  const criteriaToShow = useMemo(() => {
    const all = Array.isArray(sortedCriteria) ? sortedCriteria : [];
    if (!focusedCriterionId) return all;
    return all.filter((c) => String(c?.id) === String(focusedCriterionId));
  }, [sortedCriteria, focusedCriterionId]);

  const bandsSorted = useMemo(
    () => (bands || []).slice().sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order)),
    [bands]
  );

  const allCriteria = useMemo(() => (Array.isArray(sortedCriteria) ? sortedCriteria : []), [sortedCriteria]);

  const totalByStudentId = useMemo(() => {
    const totals = {};
    (students || []).forEach((st) => {
      if (!st || st.id == null) return;
      let total = 0;
      (allCriteria || []).forEach((c) => {
        if (!c || c.id == null) return;
        const key = `${st.id}:${c.id}`;
        const item = itemsByKey?.[String(key)];
        const s = item?.score;
        if (s === null || s === undefined || s === '') return;
        const n = Number(s);
        if (!Number.isFinite(n)) return;
        total += n;
      });
      totals[String(st.id)] = total;
    });
    return totals;
  }, [students, allCriteria, itemsByKey]);

  const fillUnsetScoresForCriterion = useCallback(
    (criterionId, scoreValue) => {
      if (readOnly || savingBulk) return;
      if (criterionId == null) return;
      if (scoreValue === null || scoreValue === undefined || scoreValue === '') return;

      if (onFillUnsetScores) {
        onFillUnsetScores({ criterionId, score: scoreValue });
        return;
      }

      // Fallback: per-cell updates (still only fills unset values)
      if (!onSetScore) return;
      const normalizedScore = Number(scoreValue);
      if (!Number.isFinite(normalizedScore)) return;

      (students || []).forEach((st) => {
        if (!st || st.id == null) return;
        const key = `${st.id}:${criterionId}`;
        const item = itemsByKey?.[String(key)];
        const s = item?.score;
        const isUnset = s === null || s === undefined || s === '';
        if (!isUnset) return;
        onSetScore(st.id, criterionId, normalizedScore);
      });
    },
    [itemsByKey, onFillUnsetScores, onSetScore, readOnly, savingBulk, students]
  );

  return (
    <TableContainer
      sx={{
        overflowX: printable ? { xs: 'auto', sm: 'visible' } : 'auto',
        overflowY: 'visible',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '@media print': { overflowX: 'visible' },
        ...(containerSx || {}),
      }}
    >
      <Table size="small" stickyHeader={!printable} sx={tableGridSx}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ width: printable ? 180 : 110, minWidth: printable ? 180 : 110, maxWidth: printable ? 260 : 110, px: 1, py: 1, fontWeight: 800, ...(printable ? {} : stickyStudentCellHeadSx) }}
            >
              Öğrenci
            </TableCell>
            {(criteriaToShow || []).map((c) => {
              const scoreValues = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
              const uniqScoreValues = getUniqueScoreValues(scoreValues, [0, 1, 2, 3]);
              const optionCount = Math.max(2, Number(uniqScoreValues?.length || 0));
              const criterionColWidth = Math.min(240, Math.max(120, optionCount * 36 + 36));

              return (
                <TableCell
                  key={String(c?.id)}
                  sx={
                    printable
                      ? {
                        width: 'auto',
                        minWidth: 120,
                        maxWidth: 'none',
                        verticalAlign: 'bottom',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.15,
                      }
                      : { width: criterionColWidth, minWidth: criterionColWidth, maxWidth: criterionColWidth, verticalAlign: 'bottom' }
                  }
                >
                {c?.section_title ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                    {String(c.section_title)}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {String(c?.name || '')}
                </Typography>

                {(() => {
                  if (printable) return null;
                  if (!uniqScoreValues || uniqScoreValues.length === 0) return null;

                  const cid = c?.id;
                  const selected = headerFillByCriterionId?.[String(cid)] ?? null;

                  return (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={selected}
                      onChange={(_, v) => {
                        if (v === null) return;
                        setHeaderFillByCriterionId((prev) => ({ ...prev, [String(cid)]: v }));
                        fillUnsetScoresForCriterion(cid, v);
                      }}
                      disabled={readOnly || savingBulk}
                      sx={{ mt: 0.75, flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1, minWidth: 32 } }}
                    >
                      {uniqScoreValues.map((v) => (
                        <ToggleButton
                          key={String(v)}
                          value={Number(v)}
                          sx={scoreToggleButtonSx(scoreToneFromValue(v, uniqScoreValues))}
                        >
                          {String(v)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  );
                })()}
                </TableCell>
              );
            })}
            <TableCell sx={{ width: 90, minWidth: 90, maxWidth: 90, fontWeight: 800, textAlign: 'center' }}>Toplam</TableCell>
            <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140, fontWeight: 800 }}>Genel</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {(students || []).map((st) => {
            const sid = st?.id;
            const total = totalByStudentId[String(sid)] ?? 0;
            const bandLabel = pickBandLabel(total, bandsSorted);
            const tone = bandTone(bandLabel);

            return (
              <TableRow key={String(sid)} hover>
                <TableCell sx={{ ...(printable ? {} : stickyStudentCellBodySx), width: printable ? 180 : 130, minWidth: printable ? 180 : 130, maxWidth: printable ? 260 : 130, px: 1, py: 0.75 }}>
                  <Tooltip
                    title={`${String(st?.student_no ?? st?.id ?? '')} — ${String(st?.first_name || '')} ${String(st?.last_name || '')}`}
                    placement="right"
                    arrow
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.15,
                          fontWeight: 700,
                          fontSize: 12.5,
                          ...(printable
                            ? { display: 'block', overflow: 'visible', textOverflow: 'clip', maxWidth: 'none', whiteSpace: 'normal' }
                            : {
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 115,
                            }),
                        }}
                      >
                        {String(st?.student_no ?? st?.id ?? '')} - {String(st?.first_name || '')} {String(st?.last_name || '')}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>

                {(criteriaToShow || []).map((c) => {
                  const key = `${sid}:${c?.id}`;
                  const item = itemsByKey?.[String(key)] || {};

                  const scoreValues = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
                  const uniqScoreValues = getUniqueScoreValues(scoreValues, [0, 1, 2, 3]);

                  const currentScore = Number.isFinite(Number(item?.score)) ? Number(item.score) : null;

                  return (
                    <TableCell key={String(c?.id)} sx={{ py: 0.75 }}>
                      {printable ? (
                        currentScore === null ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <Chip size="small" label={String(currentScore)} />
                        )
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {uniqScoreValues.length > 0 ? (
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              value={currentScore}
                              onChange={(_, v) => onSetScore && onSetScore(sid, c?.id, v)}
                              disabled={readOnly || (isSavingScore ? isSavingScore(sid, c?.id) : false)}
                              sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1, minWidth: 34 } }}
                            >
                              {uniqScoreValues.map((v) => (
                                <ToggleButton
                                  key={String(v)}
                                  value={Number(v)}
                                  sx={scoreToggleButtonSx(scoreToneFromValue(v, uniqScoreValues))}
                                >
                                  {String(v)}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          ) : (
                            <Autocomplete
                              size="small"
                              options={scoreValues}
                              value={currentScore}
                              onChange={(_, v) => onSetScore && onSetScore(sid, c?.id, v)}
                              disabled={readOnly || (isSavingScore ? isSavingScore(sid, c?.id) : false)}
                              sx={{ width: 120 }}
                              renderInput={(params) => <TextField {...params} label="Puan" />}
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}

                <TableCell sx={{ textAlign: 'center' }}>
                  <Chip size="small" label={String(total)} />
                </TableCell>

                <TableCell>
                  {bandLabel ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={tone}
                      sx={bandChipSx(tone)}
                      label={String(bandLabel)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
